import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Rocket, Clock, CheckCircle2, Loader2, Lock, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface LaunchKitPurchase {
  id: string;
  status: string;
  purchased_at: string;
  fulfilled_at: string | null;
}

export const LaunchKitDashboardCard = () => {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState<LaunchKitPurchase | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (user) loadPurchase();
  }, [user]);

  const loadPurchase = async () => {
    try {
      const { data } = await supabase
        .from('launch_kit_purchases')
        .select('id, status, purchased_at, fulfilled_at')
        .eq('user_id', user!.id)
        .order('purchased_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        setPurchase(data[0]);
      }
    } catch (err) {
      console.error('Error loading launch kit purchase:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!user || !session) {
      navigate('/login?redirect=/launch-kit');
      return;
    }

    setCheckoutLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('create-launch-kit-checkout', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start checkout';
      toast.error(msg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) return null;

  // Already purchased — show status
  if (purchase) {
    const isPending = purchase.status === 'pending' || purchase.status === 'in_progress';
    const isFulfilled = purchase.status === 'fulfilled';

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 rounded-2xl bg-card border border-accent/20 shadow-sm"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Rocket className="w-6 h-6 text-accent" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground">AI Business Launch Kit</h3>
            {isPending && (
              <>
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 px-3 py-1 rounded-full text-xs font-medium">
                    <Clock className="w-3 h-3" />
                    In Progress
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                  Your personalized business blueprint is being built by our team. 
                  You will receive it by email within <strong className="text-foreground">14 days</strong> of purchase.
                </p>
              </>
            )}
            {isFulfilled && (
              <>
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 px-3 py-1 rounded-full text-xs font-medium">
                    <CheckCircle2 className="w-3 h-3" />
                    Delivered
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  Your Launch Kit has been delivered. Check your email for the complete blueprint.
                </p>
              </>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // Not purchased — show upsell card
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative p-6 rounded-2xl bg-gradient-to-br from-accent/5 to-primary/5 border border-accent/20 shadow-sm overflow-hidden group hover:border-accent/40 transition-colors cursor-pointer"
      onClick={() => navigate('/launch-kit')}
    >
      <div className="absolute top-3 right-3">
        <span className="inline-flex items-center gap-1 bg-accent/10 text-accent px-2.5 py-1 rounded-full text-xs font-semibold">
          <Sparkles className="w-3 h-3" />
          Premium
        </span>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Rocket className="w-6 h-6 text-accent" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground">AI Business Launch Kit</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            We build your entire personalized AI business blueprint — done-for-you.
          </p>
          <Button
            size="sm"
            className="mt-4 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/launch-kit');
            }}
          >
            <Lock className="w-3.5 h-3.5 mr-1.5" />
            Unlock Premium
            <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

